import { useMemo, useState, useCallback } from "react"
import Skeleton from "components/Skeleton/Skeleton"
import CustomImage from "components/atoms/Image/CustomImage"
import { Advertisement } from "types/Advertisements"
import useDevice from "hooks/useDevice"
import Carousel from "components/Carousel/Carousel"
import Image from "next/image"

interface HomeHeroSliderProps {
    advertisements: Advertisement[]
    currentIndex: number
    isLoading?: boolean
}

const HomeHeroSlider = ({
    advertisements,
    currentIndex,
    isLoading = false,
}: HomeHeroSliderProps) => {
    const { osType } = useDevice()
    const isIosOrMac = osType === "ios" || osType === "mac"
    const fallbackImage = "/svg/v2/pass_ticket_fallback_image_light.png"
    const [loadedSlides, setLoadedSlides] = useState<Record<string, boolean>>({})

    const markSlideLoaded = useCallback((slideKey: string) => {
        setLoadedSlides((prev) => {
            if (prev[slideKey]) return prev
            return { ...prev, [slideKey]: true }
        })
    }, [])

    const deviceConfig = useMemo(
        () => ({
            height: isIosOrMac ? "h-64 md:h-96 lg:h-screen" : "aspect-video",
        }),
        [isIosOrMac]
    )

    // Use CSS backdrop-filter on iOS for better performance, fallback to blur for others
    // Reduced blur intensity for iOS to prevent GPU overload
    const blurStyle = useMemo(
        () => ({
            filter: isIosOrMac ? "blur(100px)" : "blur(200px)",
            opacity: 1,
            transform: "translateZ(0)", // Force GPU layer
        }),
        [isIosOrMac]
    )

    const staticImages = useMemo(
        () => [
            "https://cdn.pass-tickets.com/frontend/hero.png",
            "https://cdn.pass-tickets.com/frontend/hero2.png",
            "https://cdn.pass-tickets.com/frontend/Frame 1000003662.png",
        ],
        []
    )

    // Only render current and adjacent slides for performance
    const visibleIndices = useMemo(() => {
        if (advertisements.length <= 2) {
            return new Set(advertisements.map((_, i) => i))
        }
        const prev = (currentIndex - 1 + advertisements.length) % advertisements.length
        const next = (currentIndex + 1) % advertisements.length
        return new Set([prev, currentIndex, next])
    }, [currentIndex, advertisements.length])

    if (isLoading) {
        return (
            <Skeleton
                count={1}
                type="hero_image"
                className={`${deviceConfig.height} max-h-screen w-full overflow-hidden md:block`}
            />
        )
    }

    if (advertisements.length === 0) {
        return (
            <div
                className="navigation-wrapper relative bg-[#211b52]"
                style={blurStyle}
            >
                <Carousel
                    items={staticImages}
                    showDots={false}
                    showArrows={false}
                    renderSlide={(image, index) => (
                        <div
                            key={index}
                            className={`relative ${deviceConfig.height} max-h-screen w-full overflow-hidden md:block`}
                        >
                            <Image
                                src={fallbackImage}
                                alt="hero placeholder"
                                fill
                                aria-hidden="true"
                                className="absolute left-0 top-0 h-full w-full object-cover"
                                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 90vw, 1200px"
                            />
                            <CustomImage
                                src={image}
                                alt="hero image"
                                style={{ objectFit: "cover" }}
                                fill
                                priority={index === 0}
                                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 90vw, 1200px"
                                quality={85}
                                className={`transition-opacity duration-500 ${
                                    loadedSlides[`static-${index}`]
                                        ? "opacity-100"
                                        : "opacity-0"
                                }`}
                                onLoad={() => markSlideLoaded(`static-${index}`)}
                            />
                        </div>
                    )}
                    className="max-h-screen"
                />
            </div>
        )
    }

    return (
        <div
            className="navigation-wrapper relative h-full w-full bg-[#211b52]"
            style={blurStyle}
        >
            {advertisements.map((advertisement, index) => {
                const isVisible = index === currentIndex
                const shouldRender = visibleIndices.has(index)
                const slideKey = advertisement._id || `ad-${index}`
                
                // Only render current and adjacent slides to reduce memory usage on iOS
                if (!shouldRender) {
                    return null
                }
                
                return (
                    <div
                        key={slideKey}
                        className={`absolute left-0 top-0 h-full w-full transition-opacity duration-700 ${
                            isVisible ? "opacity-100 z-10" : "opacity-0 z-0"
                        }`}
                    >
                        <Image
                            src={fallbackImage}
                            alt="hero placeholder"
                            fill
                            aria-hidden="true"
                            className="absolute left-0 top-0 h-full w-full object-cover"
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 90vw, 1200px"
                        />
                        <picture className="absolute inset-0">
                            <source 
                                media="(max-width: 767px)" 
                                srcSet={`${process.env.NEXT_PUBLIC_Event_Img_URL}${advertisement.image || advertisement.web_image || ""}`}
                            />
                            <source 
                                media="(min-width: 768px)" 
                                srcSet={`${process.env.NEXT_PUBLIC_Event_Img_URL}${advertisement.web_image || ""}`}
                            />
                            <Image
                                src={`${process.env.NEXT_PUBLIC_Event_Img_URL}${advertisement.web_image || ""}`}
                                alt={advertisement.ar_description || "Advertisement"}
                                fill
                                priority={index === 0}
                                loading={index === 0 ? "eager" : "lazy"}
                                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 90vw, 1200px"
                                className="absolute left-0 top-0 h-full w-full object-cover transition-opacity duration-500"
                                style={{ transform: "translateZ(0)" }} // Force GPU compositing
                                onLoad={() => markSlideLoaded(slideKey)}
                            />
                        </picture>
                    </div>
                )
            })}

            {advertisements.length === 1 && (
                <>
                    <div className="pointer-events-none absolute bottom-0 left-1/2 flex h-full w-full -translate-x-1/2 items-end justify-center gap-2 bg-gradient-to-t from-white from-0% to-30% pb-2 dark:from-primary-theme-500" />
                    <div className="dots absolute bottom-0 left-0 flex h-5 w-full items-center justify-center gap-2 md:h-10" />
                </>
            )}
        </div>
    )
}

export default HomeHeroSlider
